import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { useCreateVendor } from '@/hooks/use-procurement';
import { formErrorText } from '@/lib/form-errors';
import type { Vendor } from '@/lib/types';

interface AddVendorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (vendor: Vendor) => void;
}

export function AddVendorDialog({ open, onOpenChange, onCreated }: AddVendorDialogProps) {
  const createVendor = useCreateVendor();
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const close = () => {
    setName('');
    setError('');
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setError('');
    try {
      const vendor = await createVendor.mutateAsync({
        name: name.trim(),
        status: 'active',
      });
      onCreated(vendor);
      close();
    } catch (err) {
      setError(
        formErrorText(err, 'The vendor could not be created. Please try again.', {
          name: 'Vendor name',
        })
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen ? onOpenChange(true) : close()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New vendor</DialogTitle>
            <DialogDescription>Create a vendor without leaving this requisition.</DialogDescription>
          </DialogHeader>
          <div data-field-container className="space-y-2 py-4">
            <Label htmlFor="new-vendor-name">Vendor name *</Label>
            <Input
              id="new-vendor-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <Button type="submit" disabled={createVendor.isPending}>
              {createVendor.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create vendor
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
